import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import CodeBlock from '../components/CodeBlock';
import { Lightbulb } from 'lucide-react';

const codeExample = `class MultiHeadAttention(nn.Module):
    """
    Foundation of LLM reasoning: attention mechanism
    Allows model to focus on relevant context
    """
    def __init__(self, d_model, num_heads):
        super().__init__()
        self.d_model = d_model
        self.num_heads = num_heads
        self.head_dim = d_model // num_heads
        
        # Query, Key, Value projections
        self.qkv_proj = nn.Linear(d_model, 3 * d_model)
        self.out_proj = nn.Linear(d_model, d_model)

    def forward(self, x):
        # Generate Q, K, V
        qkv = self.qkv_proj(x)
        
        # Scaled dot-product attention
        scores = torch.matmul(q, k.transpose(-2, -1))
        attn_weights = torch.softmax(scores, dim=-1)
        
        return self.out_proj(context)`;

const TransformerSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="The foundation of all modern LLMs"
      >
        Transformer Architecture
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Key components */}
          <div className="space-y-4">
            <h3 className="font-heading text-xl font-semibold text-foreground mb-4">Key Components</h3>
            
            <div className="space-y-3">
              {[
                'Multi-head attention mechanism',
                'Self-attention for long-range dependencies',
                'Positional encoding for sequence order',
                'Feed-forward networks'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  <span className="text-muted-foreground">{item}</span>
                </div>
              ))}
            </div>
            
            <div className="mt-6 p-4 rounded-xl bg-secondary/10 border border-secondary/20">
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-foreground">Key Discussion Points</p>
                  <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                    <li>• Self-attention enables long-range dependencies crucial for reasoning</li>
                    <li>• Multi-head attention allows parallel processing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          {/* Code example */}
          <div>
            <CodeBlock 
              code={codeExample}
              title="transformer.py"
              language="python"
            />
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default TransformerSlide;
